import { useFormik } from 'formik';
import { ToastMessageLevel } from 'views/components/atoms/toast';
import { useToast } from 'views/components/atoms/toast/hook';
import { useGlobalToastHandler } from 'views/components/organisms/globalToast/hooks';
import { useChangePasswordMutation } from 'views/utils/graphql/graphql';
import useOnceSubmit from 'views/utils/useOnceSubmit';

type ChangePasswordInput = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const initialValues: ChangePasswordInput = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const validate = (input: ChangePasswordInput) => {
  const errors = {} as { [P in keyof ChangePasswordInput]: string | undefined };
  if (!input.currentPassword) {
    errors.currentPassword = '入力してください';
  }

  if (!input.newPassword) {
    errors.newPassword = '入力してください';
  } else if (!input.newPassword.match(/^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z0-9!-/:-@[-`{-~]{8,64}$/)) {
    errors.newPassword = '8～64文字の半角英数字記号で入力してください（英字・数字それぞれ1文字以上）';
  }

  if (!input.confirmPassword) {
    errors.confirmPassword = '入力してください';
  } else if (input.newPassword !== input.confirmPassword) {
    errors.confirmPassword = 'パスワードが一致しません';
  }

  return errors;
};

export type ChangePasswordProps = {
  isModal: boolean;
  onCompleted?: () => void;
};

export const useChangePassword = ({ isModal, onCompleted }: ChangePasswordProps) => {
  const { showInfo, showError } = useGlobalToastHandler();
  const { toastProps, showToast } = useToast(); // モーダル表示している時用のトースト
  const [_res, changePasswordMutation] = useChangePasswordMutation();

  const onSubmit = useOnceSubmit<ChangePasswordInput>(
    {
      onSubmit: async (input: ChangePasswordInput) => {
        const res = await changePasswordMutation({
          input: {
            CurrentPassword: input.currentPassword,
            NewPassword: input.newPassword,
          },
        });
        if (res.data && res.data?.ChangePassword?.Code !== 0) {
          showToast(res.data?.ChangePassword?.Message ?? 'ログインに失敗しました', ToastMessageLevel.Error);
        } else if (res.data?.ChangePassword?.Code === 0) {
          showInfo('パスワードが変更されました');
          onCompleted?.();
        }
      },
      isLoadingForceCenter: isModal,
    },
    [changePasswordMutation],
  );

  const formik = useFormik<ChangePasswordInput>({
    initialValues,
    onSubmit,
    validate,
    validateOnBlur: false,
    validateOnChange: true,
  });

  return {
    formik,
    toastProps,
  };
};
